import type { DocumentReference } from "@medplum/fhirtypes";

interface DocumentCategoryDefinition {
  code: DocumentCategoryCode;
  display: string;
}

interface LegacyDocumentCategoryDefinition {
  code: string;
  display: string;
  canonicalCode: DocumentCategoryCode;
}

interface DeriveDocumentCategoryCodeInput {
  classificationTypes?: string[];
  type?: DocumentReference["type"];
  source?: string;
}

const DOCUMENT_CATEGORY_SYSTEM = "http://vibrantpractice.com/fhir/CodeSystem/document-category";

export const DocumentCategoryCode = {
  Labs: "labs",
  Imaging: "imaging",
  Microbiome: "microbiome",
  Referrals: "referrals",
  Genetics: "genetics",
  Insurance: "insurance",
  Consent: "consent",
  ClinicalNotes: "clinical-notes",
  Other: "other",
} as const;

export type DocumentCategoryCode = (typeof DocumentCategoryCode)[keyof typeof DocumentCategoryCode];

export const DocumentCategoryFilter = {
  All: "all",
} as const;

export type DocumentCategoryFilterValue =
  | DocumentCategoryCode
  | (typeof DocumentCategoryFilter)[keyof typeof DocumentCategoryFilter];

const DOCUMENT_CATEGORY_DEFINITIONS = [
  { code: DocumentCategoryCode.Labs, display: "Labs" },
  { code: DocumentCategoryCode.Imaging, display: "Imaging" },
  { code: DocumentCategoryCode.Microbiome, display: "Microbiome" },
  { code: DocumentCategoryCode.Referrals, display: "Referrals" },
  { code: DocumentCategoryCode.Genetics, display: "Genetics" },
  { code: DocumentCategoryCode.Insurance, display: "Insurance" },
  { code: DocumentCategoryCode.Consent, display: "Consent" },
  { code: DocumentCategoryCode.ClinicalNotes, display: "Clinical Notes" },
  { code: DocumentCategoryCode.Other, display: "Other" },
] as const satisfies readonly DocumentCategoryDefinition[];

const USER_VISIBLE_DOCUMENT_CATEGORY_CODES = new Set<DocumentCategoryCode>([
  DocumentCategoryCode.Labs,
  DocumentCategoryCode.Imaging,
  DocumentCategoryCode.Microbiome,
  DocumentCategoryCode.Referrals,
  DocumentCategoryCode.Genetics,
  DocumentCategoryCode.Insurance,
  DocumentCategoryCode.Consent,
  DocumentCategoryCode.ClinicalNotes,
]);

const LEGACY_DOCUMENT_CATEGORY_DEFINITIONS = [
  {
    code: "microbiome-file",
    display: "Microbiome File",
    canonicalCode: DocumentCategoryCode.Microbiome,
  },
  {
    code: "dna-file",
    display: "DNA File",
    canonicalCode: DocumentCategoryCode.Genetics,
  },
] as const satisfies readonly LegacyDocumentCategoryDefinition[];

const DOCUMENT_CATEGORY_DEFINITION_BY_CODE = new Map<
  DocumentCategoryCode,
  DocumentCategoryDefinition
>(DOCUMENT_CATEGORY_DEFINITIONS.map((definition) => [definition.code, definition]));

const LEGACY_DOCUMENT_CATEGORY_BY_CODE = new Map<string, LegacyDocumentCategoryDefinition>(
  LEGACY_DOCUMENT_CATEGORY_DEFINITIONS.map((definition) => [definition.code, definition]),
);

const CLASSIFICATION_TYPE_TO_CATEGORY_CODE = new Map<string, DocumentCategoryCode>([
  ["LabResults", DocumentCategoryCode.Labs],
  ["MicrobiomeResults", DocumentCategoryCode.Microbiome],
  ["DiagnosticSolutionsGiMapResults", DocumentCategoryCode.Microbiome],
  ["JonaGutHealthReport", DocumentCategoryCode.Microbiome],
  ["GenovaGiEffectsReport", DocumentCategoryCode.Microbiome],
  ["GutZoomerResults", DocumentCategoryCode.Microbiome],
  ["LightworkEnvironmentalResults", DocumentCategoryCode.Labs],
  ["InBodyScanResults", DocumentCategoryCode.Labs],
  ["DexaResults", DocumentCategoryCode.Imaging],
  ["Vo2MaxResults", DocumentCategoryCode.Labs],
  ["SuparResults", DocumentCategoryCode.Labs],
  ["ImagingResults", DocumentCategoryCode.Imaging],
  ["ClinicalNote", DocumentCategoryCode.ClinicalNotes],
  ["GeneticBackgroundObservation", DocumentCategoryCode.Genetics],
  ["Insurance", DocumentCategoryCode.Insurance],
]);

const DEXA_CLASSIFICATION_TYPE = "DexaResults";

/**
 * Categories that describe a document's subject rather than its kind, so a note
 * mentioning one still files as a note. See `deriveDocumentCategoryCode`.
 */
const CONTENT_CATEGORIES_OUTRANKED_BY_NOTES: DocumentCategoryCode[] = [
  DocumentCategoryCode.Labs,
  DocumentCategoryCode.Imaging,
];

const TYPE_CODING_TO_CATEGORY_CODE = new Map<string, DocumentCategoryCode>([
  ["http://loinc.org|80570-5", DocumentCategoryCode.Consent],
  ["http://loinc.org|34109-9", DocumentCategoryCode.ClinicalNotes],
  ["http://loinc.org|11506-3", DocumentCategoryCode.ClinicalNotes],
  ["http://loinc.org|57133-1", DocumentCategoryCode.Referrals],
  ["http://loinc.org|18748-4", DocumentCategoryCode.Imaging],
  ["http://snomed.info/sct|108252007", DocumentCategoryCode.Labs],
]);

const TYPE_TEXT_TO_CATEGORY_CODE = new Map<string, DocumentCategoryCode>([
  ["agreement", DocumentCategoryCode.Consent],
  ["clinical note", DocumentCategoryCode.ClinicalNotes],
  ["progress note", DocumentCategoryCode.ClinicalNotes],
  ["lab labels", DocumentCategoryCode.Labs],
  ["collection instructions", DocumentCategoryCode.Labs],
]);

const SOURCE_TEXT_TO_CATEGORY_CODE = new Map<string, DocumentCategoryCode>([
  ["typed-signature", DocumentCategoryCode.Consent],
  ["signwell", DocumentCategoryCode.Consent],
]);

/**
 * Builds a DocumentReference category with the Vibrant code system.
 */
function createDocumentCategory({
  code,
  display,
}: {
  code: string;
  display: string;
}): NonNullable<DocumentReference["category"]> {
  return [
    {
      coding: [
        {
          system: DOCUMENT_CATEGORY_SYSTEM,
          code,
          display,
        },
      ],
    },
  ];
}

/**
 * Removes existing Vibrant category codings while preserving any third-party codings.
 */
export function stripVibrantDocumentCategories({
  category,
}: {
  category?: DocumentReference["category"];
}): NonNullable<DocumentReference["category"]> {
  return (category ?? []).flatMap((concept) => {
    const remainingCodings = (concept.coding ?? []).filter(
      (coding) => coding.system !== DOCUMENT_CATEGORY_SYSTEM,
    );

    if (remainingCodings.length > 0) {
      return [{ ...concept, coding: remainingCodings }];
    }

    if (!concept.coding?.length && concept.text) {
      return [concept];
    }

    return [];
  });
}

/**
 * Returns the raw Vibrant category codes stored on the resource.
 */
export function getStoredDocumentCategoryCodes({
  category,
}: {
  category?: DocumentReference["category"];
}): string[] {
  return (category ?? [])
    .flatMap((concept) => concept.coding ?? [])
    .filter((coding) => coding.system === DOCUMENT_CATEGORY_SYSTEM)
    .map((coding) => coding.code)
    .filter((code): code is string => typeof code === "string" && code.length > 0);
}

/**
 * Normalizes canonical and legacy category codes into the current taxonomy.
 */
export function normalizeDocumentCategoryCode({
  code,
}: {
  code?: string;
}): DocumentCategoryCode | undefined {
  if (!code) {
    return undefined;
  }

  if (DOCUMENT_CATEGORY_DEFINITION_BY_CODE.has(code as DocumentCategoryCode)) {
    return code as DocumentCategoryCode;
  }

  return LEGACY_DOCUMENT_CATEGORY_BY_CODE.get(code)?.canonicalCode;
}

/**
 * Reads the normalized document category code from a resource.
 */
export function getDocumentCategoryCode({
  category,
}: {
  category?: DocumentReference["category"];
}): DocumentCategoryCode | undefined {
  for (const code of getStoredDocumentCategoryCodes({ category })) {
    const normalizedCode = normalizeDocumentCategoryCode({ code });
    if (normalizedCode) {
      return normalizedCode;
    }
  }

  return undefined;
}

/**
 * Returns whether the resource still carries one of the legacy Vibrant category codes.
 */
export function hasLegacyDocumentCategory({
  category,
}: {
  category?: DocumentReference["category"];
}): boolean {
  return getStoredDocumentCategoryCodes({ category }).some((code) =>
    LEGACY_DOCUMENT_CATEGORY_BY_CODE.has(code),
  );
}

/**
 * Gets the display label for a canonical document category code.
 */
export function getDocumentCategoryLabel({
  code,
}: {
  code?: DocumentCategoryCode;
}): string | undefined {
  return code ? DOCUMENT_CATEGORY_DEFINITION_BY_CODE.get(code)?.display : undefined;
}

/**
 * Replaces the current Vibrant category with the requested canonical category.
 */
export function upsertDocumentCategory({
  category,
  code,
}: {
  category?: DocumentReference["category"];
  code: DocumentCategoryCode;
}): DocumentReference["category"] {
  const definition = DOCUMENT_CATEGORY_DEFINITION_BY_CODE.get(code);
  if (!definition) {
    throw new Error(`Unknown document category code: ${code}`);
  }

  return [
    ...createDocumentCategory({ code: definition.code, display: definition.display }),
    ...stripVibrantDocumentCategories({ category }),
  ];
}

/**
 * Derives a canonical category from parser output or stable FHIR type/source metadata.
 */
export function deriveDocumentCategoryCode({
  classificationTypes,
  type,
  source,
}: DeriveDocumentCategoryCodeInput): DocumentCategoryCode | undefined {
  const classifiedCategoryCodes = (classificationTypes ?? [])
    .map((classificationType) => CLASSIFICATION_TYPE_TO_CATEGORY_CODE.get(classificationType))
    .filter((categoryCode): categoryCode is DocumentCategoryCode => categoryCode !== undefined);

  if (classifiedCategoryCodes.includes(DocumentCategoryCode.Microbiome)) {
    return DocumentCategoryCode.Microbiome;
  }

  if (classificationTypes?.includes(DEXA_CLASSIFICATION_TYPE)) {
    return DocumentCategoryCode.Imaging;
  }

  const firstClassifiedCategory = classifiedCategoryCodes[0];

  // A note that discusses results is still a note. `ClinicalNote` describes what the document IS;
  // `LabResults` and `ImagingResults` describe what it mentions. Without this the category falls to
  // whichever type the classifier happened to list first, and the content label wins: in one
  // migration all 105 documents classified as both note and lab landed under Labs, and a letter
  // telling a patient about their MRI landed under Imaging. Either way the clinician browsing that
  // section finds correspondence instead of results, and reads the same content twice.
  //
  // Category is display metadata only — fan-out follows raw classifier output — so the lab and
  // imaging values inside the note are still extracted; this changes only where the document files.
  //
  // Microbiome and DEXA are checked above and keep precedence: both name a specific report rather
  // than a subject a note might touch on, so a document carrying either label is that report.
  if (
    firstClassifiedCategory &&
    CONTENT_CATEGORIES_OUTRANKED_BY_NOTES.includes(firstClassifiedCategory) &&
    classifiedCategoryCodes.includes(DocumentCategoryCode.ClinicalNotes)
  ) {
    return DocumentCategoryCode.ClinicalNotes;
  }

  if (firstClassifiedCategory) {
    return firstClassifiedCategory;
  }

  for (const coding of type?.coding ?? []) {
    if (!coding.system || !coding.code) {
      continue;
    }

    const typeCategory = TYPE_CODING_TO_CATEGORY_CODE.get(`${coding.system}|${coding.code}`);
    if (typeCategory) {
      return typeCategory;
    }

    const displayCategory = coding.display
      ? TYPE_TEXT_TO_CATEGORY_CODE.get(coding.display.toLowerCase())
      : undefined;
    if (displayCategory) {
      return displayCategory;
    }
  }

  const typeTextCategory = type?.text
    ? TYPE_TEXT_TO_CATEGORY_CODE.get(type.text.toLowerCase())
    : undefined;
  if (typeTextCategory) {
    return typeTextCategory;
  }

  const normalizedSource = source?.toLowerCase();
  if (!normalizedSource) {
    return undefined;
  }

  for (const [sourceText, categoryCode] of SOURCE_TEXT_TO_CATEGORY_CODE.entries()) {
    if (normalizedSource.includes(sourceText)) {
      return categoryCode;
    }
  }

  return undefined;
}

export const DocumentCategory = {
  System: DOCUMENT_CATEGORY_SYSTEM,
  Labs: createDocumentCategory({
    code: DocumentCategoryCode.Labs,
    display: "Labs",
  }),
  Imaging: createDocumentCategory({
    code: DocumentCategoryCode.Imaging,
    display: "Imaging",
  }),
  Microbiome: createDocumentCategory({
    code: DocumentCategoryCode.Microbiome,
    display: "Microbiome",
  }),
  Referrals: createDocumentCategory({
    code: DocumentCategoryCode.Referrals,
    display: "Referrals",
  }),
  Genetics: createDocumentCategory({
    code: DocumentCategoryCode.Genetics,
    display: "Genetics",
  }),
  Insurance: createDocumentCategory({
    code: DocumentCategoryCode.Insurance,
    display: "Insurance",
  }),
  Consent: createDocumentCategory({
    code: DocumentCategoryCode.Consent,
    display: "Consent",
  }),
  ClinicalNotes: createDocumentCategory({
    code: DocumentCategoryCode.ClinicalNotes,
    display: "Clinical Notes",
  }),
  Other: createDocumentCategory({
    code: DocumentCategoryCode.Other,
    display: "Other",
  }),
} as const;

export const LegacyDocumentCategory = {
  Microbiome: createDocumentCategory({ code: "microbiome-file", display: "Microbiome File" }),
  Dna: createDocumentCategory({ code: "dna-file", display: "DNA File" }),
} as const;

export const DOCUMENT_CATEGORY_OPTIONS = DOCUMENT_CATEGORY_DEFINITIONS.filter((definition) =>
  USER_VISIBLE_DOCUMENT_CATEGORY_CODES.has(definition.code),
).map((definition) => ({
  value: definition.code,
  label: definition.display,
}));

export const DOCUMENT_CATEGORY_FILTER_OPTIONS = [
  {
    value: DocumentCategoryFilter.All,
    label: "All categories",
  },
  ...DOCUMENT_CATEGORY_OPTIONS,
] as const;
